import { HelpCircle, X } from "@untitledui/icons";
import { Fragment, type ReactNode, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Heading } from "react-aria-components";
import { Button } from "@/components/base/buttons/button";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";

interface FormatCharacter {
	symbol: string;
	meaning: string;
}

const formatCharacters: FormatCharacter[] = [
	{ symbol: "#", meaning: "any digit — 0 through 9" },
	{ symbol: "A", meaning: "any letter — a through z or A through Z" },
	{ symbol: "X", meaning: "any letter or digit, upper or lower case" },
];

interface BuildingBlock {
	pattern: string;
	meaning: string;
}

const buildingBlocks: BuildingBlock[] = [
	{ pattern: "INV-", meaning: "Anything outside brackets has to appear exactly as written, in that position." },
	{
		pattern: "[#3]",
		meaning: "A number inside brackets repeats that character exactly that many times — 3 digits, like 123.",
	},
	{ pattern: "[A+]", meaning: "A + means one or more — at least one letter, like a or abcd." },
	{ pattern: "[#3+]", meaning: "A count followed by + means that many or more — at least 3 digits." },
];

interface FormatExample {
	format: string;
	accepts: string;
	rejects: string;
}

const formatExamples: FormatExample[] = [
	{ format: "A1B[#4]", accepts: "A1B1234", rejects: "A1B123 — only 3 digits after A1B" },
	{ format: "A1B[#3+]", accepts: "A1B123, A1B1234567", rejects: "A1B12 — fewer than 3 digits after A1B" },
	{ format: "[#3]-[A4]-[#+]", accepts: "123-abcd-9, 123-abcd-987654", rejects: "123-abcd- — nothing after the last dash" },
	{ format: "([#3]) [#3]-[#4]", accepts: "(512) 555-0134", rejects: "512-555-0134" },
];

const Code = ({ children }: { children: string }) => (
	<code className="rounded bg-secondary px-1 py-0.5 font-mono text-xs whitespace-nowrap text-primary">{children}</code>
);

const Outcome = ({ label, isAccepted, value }: { label: string; isAccepted: boolean; value: string }) => (
	<div className="flex flex-wrap items-baseline gap-x-2">
		<span className={isAccepted ? "text-sm font-medium text-success-primary" : "text-sm font-medium text-error-primary"}>
			{label}
		</span>
		<span className="text-sm text-tertiary">{value}</span>
	</div>
);

const HelpSection = ({ title, children }: { title: string; children: ReactNode }) => (
	<div className="flex flex-col gap-2">
		<h4 className="text-sm font-semibold text-primary">{title}</h4>
		{children}
	</div>
);

export const ReferenceFormatHelp = () => {
	const [isOpen, setIsOpen] = useState(false);

	useHotkeys("shift+slash", () => setIsOpen(true));

	return (
		<>
			<Tooltip title="How formats work" description="Press ?" placement="top">
				<TooltipTrigger
					aria-label="How formats work"
					aria-keyshortcuts="?"
					onPress={() => setIsOpen(true)}
					className="cursor-pointer text-fg-quaternary transition duration-200 hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover"
				>
					<HelpCircle className="size-4" />
				</TooltipTrigger>
			</Tooltip>
			{isOpen && (
				<ModalOverlay isOpen onOpenChange={(open) => !open && setIsOpen(false)} className="z-[60] items-center">
					<Modal className="max-w-3xl">
						<Dialog>
							<div className="w-full rounded-lg bg-white">
								<div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
									<Heading slot="title" level={3} className="text-lg font-semibold text-gray-900">
										How formats work
									</Heading>
									<Button color="tertiary" size="sm" onClick={() => setIsOpen(false)}>
										<X className="size-5" />
									</Button>
								</div>
								<div className="flex max-h-[calc(100dvh-16rem)] flex-col gap-5 overflow-y-auto p-4">
									<p className="text-sm text-tertiary">
										A format describes what a valid value looks like. If you add a format, the talent member's input must
										match the format. No format specified means the talent member can enter any free-form text that they
										want.
									</p>
									<div className="grid gap-5 lg:grid-cols-2">
										<div className="flex flex-col gap-5">
											<HelpSection title="Characters">
												<dl className="grid grid-cols-[auto_1fr] items-baseline gap-x-3 gap-y-1.5">
													{formatCharacters.map((character) => (
														<Fragment key={character.symbol}>
															<dt>
																<Code>{character.symbol}</Code>
															</dt>
															<dd className="text-sm text-tertiary">{character.meaning}</dd>
														</Fragment>
													))}
												</dl>
											</HelpSection>
											<HelpSection title="Building blocks">
												<dl className="grid grid-cols-[auto_1fr] items-baseline gap-x-3 gap-y-2">
													{buildingBlocks.map((block) => (
														<Fragment key={block.pattern}>
															<dt>
																<Code>{block.pattern}</Code>
															</dt>
															<dd className="text-sm text-tertiary">{block.meaning}</dd>
														</Fragment>
													))}
												</dl>
											</HelpSection>
										</div>
										<HelpSection title="Examples">
											<ul className="flex flex-col gap-3">
												{formatExamples.map((example) => (
													<li key={example.format} className="flex flex-col gap-1">
														<div>
															<Code>{example.format}</Code>
														</div>
														<Outcome label="Accepts" isAccepted value={example.accepts} />
														<Outcome label="Rejects" isAccepted={false} value={example.rejects} />
													</li>
												))}
											</ul>
										</HelpSection>
									</div>
									<HelpSection title="Good to know">
										<ul className="flex list-disc flex-col gap-1 pl-5 text-sm text-tertiary">
											<li>The whole value has to match — anything extra before or after is rejected.</li>
											<li>Upper and lower case are treated the same.</li>
											<li>Add several formats when a value can arrive more than one way. Matching any one of them is enough.</li>
										</ul>
									</HelpSection>
								</div>
								<div className="flex border-t border-gray-200 px-4 py-3">
									<Button color="primary" className="flex-1" onClick={() => setIsOpen(false)}>
										Got it
									</Button>
								</div>
							</div>
						</Dialog>
					</Modal>
				</ModalOverlay>
			)}
		</>
	);
};
